<template>
  <section class="appointment-schedule-and-companion evollu-form">

    <div class="d-flex justify-content-between align-items-center mb-3">
      <h6 class="is-bold mb-0"> {{$t('clinical.appointments.appointment_data')}}</h6>
      <div v-if="hasUnsavedChanges">
        <small class="text-orange is-bold">
          {{$t('clinical.people.anamnesis.unsaved')}}
        </small>
      </div>
    </div>

    <div class="row">
      <div class="col-12 col-md-4">
        <div class="appointment-schedule-data d-flex flex-column flex-sm-row align-items-center py-2">
          <i class="fa fa-clock-o d-none d-sm-block ml-1 mr-4"></i>
          <div class="is-bold">
            <span>{{$options.filters.localTime(appointment.start, 'DD MMMM YYYY', '', locale)}}</span>
          </div>

          <div class="is-bold ml-sm-3">
            <span>{{$options.filters.localTime(appointment.start, 'HH:mm')}}</span>
            <span class="is-bold mx-2">&nbsp;-&nbsp;</span>
            <span>{{$options.filters.localTime(appointment.end, 'HH:mm')}}</span>
          </div>
        </div>
      </div>
      <div class="col-12 col-md-4" v-if="editing">
        <multiselect
          v-model="appointment.type"
          :options="appointmentTypesOptions"
          @input="val => updateValue('type_id', val ? val.id : null)"
          :customLabel="option => getAppointmentTypeLabel(option)"
          track-by="id"
          select-label=""
          selected-label=""
          deselect-label=""
          :placeholder="$t('clinical.appointments.type')"
          :allow-empty="false"
          :multiple="false"
          :searchable="true"
          :disabled="!(appointment.type_id === null || (appointment.type_id !== null && userIsCreator))"
          open-direction="bottom"
          >
          <span slot="noResult">{{ $t('patients.noResults') }}</span>
        </multiselect>
        <small v-show="formErrors.has('type_id')" class="help text-danger">
          {{ formErrors.get('type_id') | translate}}
        </small>
      </div>
    </div>

    <!-- Marketing Code -->
    <div class="row">
      <div class="col-12 col-md-6">
        <va-input
          id="marketing-code"
          :value="value.marketing_code"
          :label="$t('clinical.appointments.marketing_code')"
          :placeholder="$t('clinical.appointments.marketing_code')"
          @input="val => updateValue('marketing_code', val)"
        />

        <small v-show="formErrors.has('marketing_code')" class="help text-danger">
          {{ $t(formErrors.get('marketing_code')) }}
        </small>
      </div>
    </div>

    <!-- Has Companion -->
    <div v-if="editing" class="evollu-form row align-items-center py-3">
      <div class="col-12 col-md-4">
        <va-checkbox
          :value="value.has_companion"
          @input="onInputHasCompanion"
          :label="$t('clinical.appointments.has_companion')"
          :isCircle="true"
        />
      </div>

      <div class="col-12 col-md-3" v-if="value.has_companion">
        <span class="select is-fullwidth is-small">
          <va-checkbox
            :label="$t('clinical.appointments.has_companion_clinical_file')"
            id="checkbox-has-company-with-clinical-file"
            :isCircle="true"
            :value="value.has_companion_person"
            @input="onInputHasCompanionPerson"/>
        </span>
      </div>
    </div>

    <div class="row" v-if="value.has_companion">
        <!-- Companion Input -->
        <div class="col-12 col-md-9" v-if="!value.has_companion_person">
          <va-input
            id="appointment-companion-name"
            :value="value.companion"
            @input="val => updateValue('companion', val)"
            :placeholder="$t('clinical.appointments.companion_name')"
            :disabled="!value.has_companion"
          />
        </div>

        <!--Companion Person Search -->
        <div class="col-12 col-md-9" v-if="value.has_companion && value.has_companion_person">
          <div class="form-group" :class="{'has-error': formErrors.has('companion_person_id')}" style="margin-bottom: 1rem;">
            <div class="input-group person-input-group">
              <multiselect
                v-model="value.companion_person"
                @input="val => updateValue('companion_person', val)"
                :options="availablePeople"
                track-by="id"
                select-label=""
                selected-label=""
                :loading="isFetchingServer"
                :internal-search="false"
                :clear-on-select="false"
                :close-on-select="true"
                :options-limit="10"
                :limit="3"
                :limit-text="(count) => $t('tables.multiselect_limitText_and') + count + this.$t('tables.multiselect_limitText_others')"
                deselect-label=""
                :allow-empty="true"
                :placeholder="$t('clinical.appointments.companion_name')"
                :multiple="false"
                :searchable="true"
                :show-no-options="false"
                @search-change="searchPerson"
                open-direction="bottom">
                <template slot="placeholder">
                  <div class="d-flex align-items-center">
                    <span class="i-custom-search p-2"></span>&nbsp;
                    {{ $t('clinical.appointments.search_patient') }}
                  </div>
                </template>
                <template slot="option" slot-scope="{option}">
                  <span>{{ `${option.name}` }}</span>
                  <span v-if="option.process_id"> - {{ option.process_id }}</span>
                </template>
                <template slot="singleLabel" slot-scope="{option}">
                  <span class="i-custom-search p-2"></span>&nbsp;
                  <span>{{ `${option.first_name}` }}</span>
                  <span v-if="option.last_name"> {{ `${option.last_name}`}}</span>
                </template>
                <span slot="noResult">{{'patients.noResults'| translate}}</span>
              </multiselect>
              <small v-show="formErrors.has('companion_person_id')" class="help text-danger">
                {{ formErrors.get('companion_person_id') | translate}}
              </small>
            </div>
          </div>
        </div>

        <!--Relation type -->
        <div class="col-12 col-md-3">
          <multiselect
            :value="relationshipTypesOptions.find(relation => relation.id === value.relationship_type_id)"
            :options="relationshipTypesOptions"
            @input="val => updateValue('relationship_type_id', val.id)"
            :customLabel="option => getRelationshipTypeLabel(option)"
            track-by="id"
            select-label=""
            selected-label=""
            deselect-label=""
            :placeholder="$t('clinical.appointments.relationship_type')"
            :allow-empty="true"
            :multiple="false"
            :searchable="true"
            :disabled="!value.has_companion">
            <span slot="noResult">{{ $t('patients.noResults') }}</span>
            <template slot="singleLabel" slot-scope="{option}">
              <span>{{ $t(option.name) }}</span>
            </template>
          </multiselect>
          <small v-show="formErrors.has('relationship_type_id')" class="help text-danger">
            {{ formErrors.get('relationship_type_id') | translate}}
          </small>
        </div>
    </div>

    <div v-else-if="value.has_companion">
      <div class="appointment-companion-data">
        <label>
          {{ $t('clinical.appointments.companion_name') }}:
        </label>
        <span class="is-bold" v-if="value.companion">
          {{ value.companion }}
          <span v-if="selectedRelationshipType">
            ({{ $t(`${selectedRelationshipType.name}`) }})
          </span>
        </span>
        <span class="is-bold" v-else-if="selectedRelationshipType">
          {{ $t(selectedRelationshipType.name) }}
        </span>
        <span class="is-bold" v-else>
          {{ $t('clinical.appointments.unassigned_companion') }}
        </span>
      </div>
    </div>

  </section>
</template>

<script>
import CurrentLocaleMixin from '@/mixins/CurrentLocaleMixin'
import Multiselect from 'vue-multiselect'
import { AppointmentService, PersonService } from '@/api/AuthClinicalService'
import { RELATIONSHIP_TYPES } from './config'
import { debounce } from 'lodash'
const PEOPLE_QUERY_LIMIT = 10
export default {
  props: {
    appointment: {
      type: Object,
      required: true
    },
    value: {
      type: Object,
      required: false
    },
    editing: {
      type: Boolean,
      required: false,
      default: false
    },
    service: {
      type: Object,
      required: true
    },
    formErrors: {
      type: Object,
      required: false
    }
  },
  components: {
    Multiselect
  },
  mixins: [
    CurrentLocaleMixin
  ],
  data: function () {
    return {
      availableAppointmentTypes: [],
      relationshipTypesOptions: RELATIONSHIP_TYPES,
      availablePeople: [],
      isFetchingServer: false,
      hasUnsavedChanges: false
    }
  },
  methods: {
    searchPerson: debounce(function (query = '') {
      this.isFetchingServer = true
      PersonService.query(this.service.url, { limit: PEOPLE_QUERY_LIMIT, query })
        .then(people => {
          this.availablePeople = people.filter(p => p.id !== this.appointment.person.id)
        }).catch(errors => {
          console.log('errors', errors)
        }).finally(() => {
          this.isFetchingServer = false
        })
    }, 500),
    getRelationshipTypeLabel: function (option) {
      return this.$t(option.name)
    },
    getAppointmentTypeLabel: function (option) {
      let label = option.name
      if (option.slug && option.name !== option.slug) {
        label += ` (${option.slug})`
      }
      return label
    },
    loadAppointmentTypes: function () {
      AppointmentService.getTypes(this.service.url)
        .then(types => {
          this.availableAppointmentTypes = types.filter(type => type.active)
        }).catch(errors => {
          console.log(errors)
        })
    },
    getHasUnsavedChanges: function () {
      return this.hasUnsavedChanges
    },
    setHasUnsavedChanges: function (value) {
      this.hasUnsavedChanges = value
    },
    getDefaultValue: function () {
      return {
        has_companion: false,
        companion: null,
        type_id: null
      }
    },
    updateValue: function (field, value) {
      this.$emit('input', { ...this.value, [field]: value })
      this.hasUnsavedChanges = true
      this.$emit('clearErrors', field)
    },
    onInputHasCompanionPerson: function (value) {
      this.updateValue('has_companion_person', value)
      if (!value) {
        this.$nextTick(() => {
          this.updateValue('relationship_type_id', null)
          this.$nextTick(() => {
            this.updateValue('companion_person', null)
          })
        })
      }
    },
    onInputHasCompanion: function (value) {
      this.updateValue('has_companion', value)
      if (!value) {
        this.$nextTick(() => {
          this.$emit('input', { ...this.value, has_companion_person: false, companion: null, companion_person: null, relationship_type_id: null })
        })
      }
    }
  },
  watch: {
    editing: {
      handler: function (editing) {
        if (editing) {
          this.$emit('input', { ...this.getDefaultValue(), ...this.value })
        }
      },
      immediate: true
    }
  },
  created () {
    this.loadAppointmentTypes()
  },
  computed: {
    selectedRelationshipType: function () {
      if (!this.appointment || !this.appointment.relationship_type_id) {
        return null
      }
      return this.relationshipTypesOptions.find(relation => relation.id === this.appointment.relationship_type_id)
    },
    appointmentTypesOptions: function () {
      let appointmentTypes = this.availableAppointmentTypes
      if (this.appointment.activity) {
        appointmentTypes = appointmentTypes.filter(type => type.activity_id === this.appointment.activity.id)
      }
      return appointmentTypes
    },
    userIsCreator: function () {
      return this.appointment !== null &&
      this.appointment !== undefined &&
      this.appointment.created_by !== null &&
      this.appointment.created_by.id === this.$auth.user().id
    }
  }
}
</script>
